/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * The category card's Owners block (contract #115, 2026-09-11): the product page's aggregate
 * shape — "x of n would buy again", then "n owners · k photo-verified · owners report ~h per
 * charge vs h claimed" — in COUNTS only, never percentages on a card (#52/#66), plus the
 * notes the card's carousel pages through (#118): the stored order's first three, sliced and
 * never re-ranked, so #54's sentiment-blindness survives. The aggregate reads stay on the view
 * as the LABEL source for each slide's per-owner line; the card stopped drawing their bars
 * (#118) — the PDP keeps those. Pure; tested in `tests/owner-card-owners.spec.ts`.
 */
import {
  type OwnerCardNote,
  type OwnerCardOwners,
  type OwnerReadAggregate,
  type OwnerReadValue,
} from 'lib/dealhunt/types'
import type { CommunityFeedItem } from 'lib/community/types'
import { capitalize, lowerFirst } from './band-copy'

export interface CardOwnersView {
  count: number
  /** Null when only the count is known (the own tile fed from a partial public page). */
  buyAgain: { yes: number; total: number } | null
  photoVerified: number
  /** Answered reads in question-set order — the label source for `noteReads` (#118). */
  reads: OwnerReadAggregate[]
  hoursMedian: number | null
  /** The carousel's slides in stored order (#118); empty when the payload carries no note.
   *  The category card and the review post read these; the guide's Owners block reads `items`. */
  notes: OwnerCardNote[]
  /** A guide pick's newest ≤3 reviews as community feed items, newest first (contract #217) —
   *  the guide's Owners block rows (`PickOwners` → `FeedRow`). Empty when DealHunt sent none
   *  (before #217's republish, or any card payload): the block shows its summary line alone. */
  items: CommunityFeedItem[]
}

/** One owner's verdict on one read, labelled from the aggregate — a carousel slide's line. */
export interface CardNoteRead {
  id: string
  label: string
  value: OwnerReadValue
}

export function cardOwnersFromCard(
  owners: OwnerCardOwners | null | undefined
): CardOwnersView | null {
  if (!owners || owners.count <= 0) {
    return null
  }
  return {
    count: owners.count,
    buyAgain: owners.buy_again,
    photoVerified: owners.photo_verified,
    reads: owners.reads ?? [],
    hoursMedian: owners.hours_median ?? null,
    // #118 ships `top_notes`; a payload (or an edge-cached listing) written before it carries
    // only `top_note`, and then the carousel is a single slide with no control.
    notes: owners.top_notes ?? (owners.top_note ? [owners.top_note] : []),
    items: owners.items ?? [],
  }
}

export type OwnersVerdictTone = 'up' | 'mixed' | 'down'

/** Steam's bands, minus its review-volume tiers ("Very", "Overwhelmingly"). */
const VERDICT_BANDS: Array<{ min: number; word: string; tone: OwnersVerdictTone }> = [
  { min: 0.8, word: 'Positive', tone: 'up' },
  { min: 0.6, word: 'Mostly positive', tone: 'up' },
  { min: 0.4, word: 'Mixed', tone: 'mixed' },
  { min: 0.2, word: 'Mostly negative', tone: 'down' },
  { min: 0, word: 'Negative', tone: 'down' },
]

/** Buy-again answers before the word shows; below it the review row counts and shows how many
 *  to go (design 2026-10-29). The design said 10; set to 5 the same day. Was 3, tagged
 *  "Early" until 10. */
export const OWNERS_VERDICT_MIN = 5

/**
 * A Steam-style word for the buy-again split ("Mostly positive", "Mixed") for the review page's
 * owner row, from `OWNERS_VERDICT_MIN` answers. Below the card's percentage gate (#52) on
 * purpose; the card stays counts-only.
 */
export function ownersVerdict(
  buyAgain: CardOwnersView['buyAgain']
): { word: string; tone: OwnersVerdictTone } | null {
  if (!buyAgain || buyAgain.total < OWNERS_VERDICT_MIN) {
    return null
  }
  const share = buyAgain.yes / buyAgain.total
  const band = VERDICT_BANDS.find(b => share >= b.min) ?? VERDICT_BANDS[VERDICT_BANDS.length - 1]
  return { word: band.word, tone: band.tone }
}

export function ownersHeadline(o: CardOwnersView): string {
  if (o.buyAgain && o.buyAgain.total > 0) {
    return `${o.buyAgain.yes} of ${o.buyAgain.total} would buy again`
  }
  return `${o.count} owner${o.count === 1 ? '' : 's'}`
}

/** "4 owners · 1 photo-verified · owners report ~6 h per charge vs 7.5 h claimed". */
export function ownersSubline(o: CardOwnersView, claimedHours: number | null): string {
  const parts: string[] = []
  // The headline already says the count when buy-again is unknown.
  if (o.buyAgain && o.buyAgain.total > 0) {
    parts.push(`${o.count} owner${o.count === 1 ? '' : 's'}`)
  }
  if (o.photoVerified > 0) {
    parts.push(`${o.photoVerified} photo-verified`)
  }
  if (o.hoursMedian !== null) {
    const reported = `owners report ~${o.hoursMedian} h per charge`
    parts.push(claimedHours !== null ? `${reported} vs ${claimedHours} h claimed` : reported)
  }
  return parts.join(' · ')
}

/**
 * The one read a slide shows (#118, readable pass 2026-09-12): the first read in question-set
 * order where THIS owner departed from expectation, else the first they answered at all.
 *
 * One, not two: the labels are admin-authored question-set sentences ("Comfort over long
 * sessions", "Fit and build after months") and at a 123px slide none of them fits on one line
 * at any legible size — the previous two-per-line row truncated mid-word. Departure-first is
 * deliberately POLARITY-NEUTRAL (question order decides between a better and a worse, never
 * sentiment) so the card never leans on an owner's answer the way #54 forbids leaning on
 * their note. Null when the owner skipped every read.
 */
export function noteRead(o: CardOwnersView, note: OwnerCardNote): CardNoteRead | null {
  const mine = note.reads
  if (!mine) {
    return null
  }
  let firstAnswered: CardNoteRead | null = null
  for (const read of o.reads) {
    const value = mine[read.id]
    if (!value) {
      continue
    }
    if (value !== 'expected') {
      return { id: read.id, label: read.label, value }
    }
    firstAnswered = firstAnswered ?? { id: read.id, label: read.label, value }
  }
  return firstAnswered
}

/** Every read THIS owner answered, in question-set order, labelled from the aggregate — the
 *  guide row's chips (the card has room for one: `noteRead`). */
export function noteReads(o: CardOwnersView, note: OwnerCardNote): CardNoteRead[] {
  const mine = note.reads
  if (!mine) {
    return []
  }
  const out: CardNoteRead[] = []
  for (const read of o.reads) {
    const value = mine[read.id]
    if (value) {
      out.push({ id: read.id, label: read.label, value })
    }
  }
  return out
}

/**
 * The read-value vocabulary, once for every owner surface: the chip word ("battery better"),
 * the form's and the legend's capitalised label ("As expected"), and the verdict phrase.
 */
export const READ_WORD: Readonly<Record<OwnerReadValue, string>> = {
  better: 'better',
  expected: 'as expected',
  worse: 'worse',
}

/** "Better" / "As expected" / "Worse" — the note form's segments and the read bars' legend. */
export const READ_LABEL: Readonly<Record<OwnerReadValue, string>> = {
  better: capitalize(READ_WORD.better),
  expected: capitalize(READ_WORD.expected),
  worse: capitalize(READ_WORD.worse),
}

/** Plain words, never a glyph: "▴" next to a truncated label told a reader nothing. */
export function readVerdict(value: OwnerReadValue): string {
  return value === 'expected' ? READ_WORD.expected : `${READ_WORD[value]} than expected`
}

/** The read's phrase for a sentence, article included — mirrors DealHunt's question-set `short`
 *  (server/services/ownerNotes/categoryQuestionSets.ts), which the card payload does not carry yet;
 *  unknown ids fall back to the admin label, lower-cased. */
const READ_PHRASE: Readonly<Record<string, string>> = {
  comfort: 'comfort',
  anc: 'the noise cancelling',
  battery: 'the battery',
  mic: 'the mic',
  build: 'the fit and build',
  connection: 'the wireless connection',
  positional: 'the positional audio',
  isolation: 'the isolation',
  cable: 'the cable',
}

export function readPhrase(read: { id: string; label: string }): string {
  return READ_PHRASE[read.id] ?? lowerFirst(read.label)
}

/** One owner's read as a sentence of their own, never a grade of the product:
 *  worse → "Expected more from the noise cancelling"
 *  better → "The battery beat their expectations"
 *  expected → "Nothing surprising about the fit and build" */
export function readSentence(read: CardNoteRead): string {
  const phrase = readPhrase(read)
  if (read.value === 'worse') {
    return `Expected more from ${phrase}`
  }
  if (read.value === 'better') {
    return `${capitalize(phrase)} beat their expectations`
  }
  return `Nothing surprising about ${phrase}`
}
